<template>
  <!--
    ข้อมูลประกอบคำขอจัดสรรงบประมาณค่าตอบแทนพนักงานราชการ งวด 1 (1 ต.ค. – 31 ธ.ค.) — govem_budget_request.php
    ตัดผู้ครบเกษียณอายุ (30 ก.ย. ปีงบที่เพิ่งสิ้นสุด) และผู้ลาออก/พ้นสภาพก่อน 1 ต.ค. ออก ก่อนรวมยอด
    ใช้ผ่าน ref: open()
  -->
  <v-dialog v-model="dialog" max-width="1250" scrollable>
    <v-card>
      <v-toolbar dense dark flat color="green darken-3" class="flex-grow-0">
        <v-icon left>mdi-cash-multiple</v-icon>
        <v-toolbar-title class="subtitle-1">คำขอจัดสรรงบประมาณค่าตอบแทนพนักงานราชการ งวด 1 (ต.ค. – ธ.ค.)</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <div class="d-flex flex-wrap align-center px-4 pt-3" style="gap:8px">
        <v-select v-model="fy" :items="years" dense outlined hide-details label="ปีงบประมาณ" style="max-width:160px" @change="load" />
        <span v-if="data" class="body-2">
          งวด {{ thai(data.period.from) }} – {{ thai(data.period.to) }} · ตัดผู้ครบเกษียณอายุ 30 ก.ย. {{ data.excluded_retire_fy }}
          และผู้ลาออก/พ้นสภาพก่อน {{ thai(data.period.from) }}
        </span>
        <v-spacer />
        <v-btn small color="success" :loading="exporting" :disabled="!data" @click="exportExcel">
          <v-icon small left>mdi-microsoft-excel</v-icon>ส่งออก Excel
        </v-btn>
      </div>
      <v-card-text class="pt-3" style="height:72vh">
        <v-progress-linear v-if="loading" indeterminate color="green" class="mb-2" />
        <template v-if="data">
          <v-row dense>
            <v-col v-for="t in tiles" :key="t.label" cols="6" md="2">
              <v-card outlined class="pa-2 text-center">
                <div class="caption grey--text">{{ t.label }}</div>
                <div class="subtitle-1 font-weight-bold" :class="t.cls">{{ t.value }}</div>
              </v-card>
            </v-col>
          </v-row>
          <v-alert type="info" dense text class="caption mt-2">
            ค่าตอบแทนรายเดือนใช้ค่าปัจจุบันในทะเบียนบุคคล (ปรับตามประวัติเงินเดือน/ผลการเลื่อนที่บันทึกแล้ว) ถ้าไม่มีใช้ค่าในกรอบ ·
            ยอดงวด = รายเดือน × 3 · ผู้ครบเกษียณปลายปีงบประมาณ {{ data.fiscal_year }} ยังรับค่าตอบแทนงวดนี้ตามปกติ
            <span v-if="data.totals.no_birth"> · ไม่มีวันเกิดในกรอบ {{ data.totals.no_birth }} คน (ตรวจสอบสถานะเกษียณไม่ได้ — นับรวมไว้)</span>
          </v-alert>
          <v-tabs v-model="tab" class="mb-2">
            <v-tab>รายหน่วยงาน</v-tab>
            <v-tab>ผู้ถูกตัดออก ({{ data.excluded.length }})</v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="ค้นหาหน่วยงาน"
                class="mb-2" style="max-width:320px" />
              <v-data-table :headers="unitHeaders" :items="data.units" :search="search" dense :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 100, -1] }" sort-by="retired" sort-desc>
                <template v-slot:[`item.college_name`]="{ item }">
                  {{ item.college_name }}<div class="caption grey--text">{{ item.college_code }}</div>
                </template>
                <template v-slot:[`item.retired`]="{ item }">
                  <span :class="item.retired ? 'error--text font-weight-bold' : 'grey--text'">{{ item.retired }}</span>
                  <div v-if="item.retired" class="caption">−{{ money(item.retired_salary * 3) }}</div>
                </template>
                <template v-slot:[`item.resigned`]="{ item }">
                  <span :class="item.resigned ? 'error--text' : 'grey--text'">{{ item.resigned }}</span>
                </template>
                <template v-slot:[`item.monthly`]="{ item }">{{ money(item.monthly) }}</template>
                <template v-slot:[`item.quarter`]="{ item }"><strong>{{ money(item.quarter) }}</strong></template>
              </v-data-table>
            </v-tab-item>
            <v-tab-item>
              <v-data-table :headers="excludedHeaders" :items="data.excluded" dense :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 100, -1] }">
                <template v-slot:[`item.full_name`]="{ item }">
                  {{ item.full_name }}<div class="caption grey--text">{{ item.id_card }} · ตำแหน่ง {{ item.id_position }}</div>
                </template>
                <template v-slot:[`item.note`]="{ item }">
                  <v-chip x-small dark :color="item.reason === 'retired' ? 'deep-orange' : 'grey darken-1'">
                    {{ item.reason === "retired" ? "เกษียณ" : "ลาออก/พ้นสภาพ" }}
                  </v-chip>
                  <span class="caption ml-1">{{ item.note }} · เกิด {{ item.birth || "-" }}</span>
                </template>
                <template v-slot:[`item.salary`]="{ item }">{{ money(item.salary) }}</template>
              </v-data-table>
              <v-alert type="warning" dense text class="caption mt-2">
                ผู้เกษียณที่ยังอยู่ในกรอบอัตรากำลัง ควรดำเนินการพ้นสภาพ (คืนตำแหน่งว่าง) เพื่อให้กรอบและงบประมาณงวดถัดไปถูกต้อง
              </v-alert>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="5000">{{ snack.text }}</v-snackbar>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";

const MONTHS = ["", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

export default {
  name: "GovemBudgetRequest",
  data() {
    const now = new Date();
    // ปีงบประมาณ: ต.ค.–ธ.ค. เป็นของปีงบถัดไป
    const fy = now.getFullYear() + 543 + (now.getMonth() >= 9 ? 1 : 0);
    return {
      ApiKey: "HRvec2021",
      dialog: false,
      loading: false,
      exporting: false,
      fy,
      years: [fy + 1, fy, fy - 1, fy - 2],
      data: null,
      tab: 0,
      search: "",
      snack: { show: false, text: "", color: "success" },
      unitHeaders: [
        { text: "หน่วยงาน", value: "college_name" },
        { text: "อัตรา", value: "positions", align: "right" },
        { text: "ครอง", value: "filled", align: "right" },
        { text: "เกษียณ (ตัดออก)", value: "retired", align: "right" },
        { text: "ลาออก (ตัดออก)", value: "resigned", align: "right" },
        { text: "รับค่าตอบแทน (คน)", value: "paid", align: "right" },
        { text: "รายเดือน (บาท)", value: "monthly", align: "right" },
        { text: "งวด 3 เดือน (บาท)", value: "quarter", align: "right" }
      ],
      excludedHeaders: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "หน่วยงาน", value: "college_name" },
        { text: "ตำแหน่ง", value: "name_postion" },
        { text: "เหตุที่ตัดออก", value: "note" },
        { text: "ค่าตอบแทน/เดือน", value: "salary", align: "right" }
      ]
    };
  },
  computed: {
    tiles() {
      const t = this.data.totals;
      return [
        { label: "อัตรา / ครอง", value: `${this.money(t.positions)} / ${this.money(t.filled)}` },
        { label: "ตัดผู้เกษียณ", value: `${this.money(t.retired)} คน`, cls: "deep-orange--text" },
        { label: "ตัดผู้ลาออก", value: `${this.money(t.resigned)} คน` },
        { label: "รับค่าตอบแทนงวดนี้", value: `${this.money(t.paid)} คน` },
        { label: "ยอดงวด (3 เดือน)", value: this.money(t.quarter), cls: "green--text text--darken-3" },
        { label: "ลดลงจากยอดเดิม", value: this.money(t.saved_quarter), cls: "error--text" }
      ];
    }
  },
  methods: {
    money(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    thai(iso) {
      const [y, m, d] = String(iso).split("-").map(Number);
      return `${d} ${MONTHS[m]} ${y + 543}`;
    },
    async api(extra = {}) {
      try {
        const res = await this.$http.post("govem_budget_request.php", { ApiKey: this.ApiKey, action: "quarter1", fiscal_year: this.fy, ...extra });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    open() {
      this.dialog = true;
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        this.data = await this.api();
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const d = await this.api({ include_persons: 1 });
        const t = d.totals;
        const title = `คำขอจัดสรรงบประมาณค่าตอบแทนพนักงานราชการ งวด 1 (${this.thai(d.period.from)} – ${this.thai(d.period.to)}) ปีงบประมาณ ${d.fiscal_year}`;
        const wb = XLSX.utils.book_new();
        const units = [
          [title], [`ตัดผู้ครบเกษียณอายุ 30 ก.ย. ${d.excluded_retire_fy} และผู้ลาออก/พ้นสภาพก่อน ${this.thai(d.period.from)}`], [],
          ["รหัส", "หน่วยงาน", "อัตรา", "ครองตำแหน่ง", "อัตราว่าง", "เกษียณ (ตัดออก)", "ค่าตอบแทนผู้เกษียณ/เดือน", "ลาออก (ตัดออก)",
            "ค่าตอบแทนผู้ลาออก/เดือน", "รับค่าตอบแทน (คน)", "ค่าตอบแทนรวม/เดือน", "ยอดงวด 3 เดือน", "ยอดเดิมก่อนตัด (3 เดือน)"],
          ...d.units.map(u => [u.college_code, u.college_name, u.positions, u.filled, u.vacant, u.retired, u.retired_salary, u.resigned,
            u.resigned_salary, u.paid, u.monthly, u.quarter, u.frame_monthly_all * 3]),
          ["", "รวม", t.positions, t.filled, t.positions - t.filled, t.retired, t.retired_salary, t.resigned, t.resigned_salary, t.paid,
            t.monthly, t.quarter, t.frame_monthly_all * 3]
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(units), "รายหน่วยงาน");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(d.excluded.map((e, i) => ({
          ลำดับ: i + 1, รหัสหน่วยงาน: e.college_code, หน่วยงาน: e.college_name, เลขบัตรประชาชน: e.id_card, "ชื่อ-สกุล": e.full_name,
          เลขที่ตำแหน่ง: e.id_position, ตำแหน่ง: e.name_postion, วันเกิด: e.birth || "", เหตุที่ตัดออก: e.note, "ค่าตอบแทน/เดือน": e.salary
        }))), "ผู้ถูกตัดออก");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(d.persons.map((p, i) => ({
          ลำดับ: i + 1, รหัสหน่วยงาน: p.college_code, หน่วยงาน: p.college_name, เลขบัตรประชาชน: p.id_card, "ชื่อ-สกุล": p.full_name,
          เลขที่ตำแหน่ง: p.id_position, ตำแหน่ง: p.name_postion, "ค่าตอบแทน/เดือน": p.salary, "งวด 3 เดือน": p.quarter,
          ปีงบประมาณที่เกษียณ: p.retire_fy || "", หมายเหตุ: p.no_birth ? "ไม่มีวันเกิดในกรอบ" : ""
        }))), "รายบุคคล");
        XLSX.writeFile(wb, `คำขอจัดสรรงบ_งวด1_ปีงบ${d.fiscal_year}.xlsx`);
      } catch (e) {
        this.snack = { show: true, text: "ส่งออกไม่สำเร็จ: " + e.message, color: "error" };
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>
